import { useEffect, useState } from "react";
import { SearchField } from "../../types/type";
import { GetProposalParams } from "../../api/gms/proposal/proposal-api-request";
import { DropdownOption } from "../../features/auth/auth-state";
import { useGetRequestAmendmentsQuery } from "../../api/request-amendment/request-amendment-api";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { Option } from "../../features/it-user-forms/user-form-state";
import { options } from "../../data/group-insurance";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { TotalPages, totalPages } from "../../features/pagination/pagination-inital-state";

export const useRequestAmendmentData = () => {
    
    const includedOptionIds = [1, 3, 4, 5];
    const normalizedOptionsData: Option[] = options.filter((option) => includedOptionIds.includes(option.id)).map((option) => ({ 
        id: option.id, 
        value: option.value,
        name: option.name,
    }));
    
    const normalizedOptions: DropdownOption[] = options.filter((option) => includedOptionIds.includes(option.id)).map((option) => ({ 
        id: option.id, 
        name: option.name 
    }));
    
    const [searchType, setSearchType] = useState<{ id: number; name: string; value: string; } | null>(normalizedOptionsData[0]);
    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name, } : null;
    const setSelectedSearchType = (type: { id: number; name: string } | null) => {
        if (!type) {
            setSearchType(null);
            return;
        }

        const option = normalizedOptionsData.find(o => o.id === type.id) ?? null;
        setSearchType(option);
    };
    const [searchValue, setSearchValue] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [defaultTotalPages, setDefaultTotalPages] = useState<TotalPages>(totalPages[0])
    const [isPageSizeChanging, setIsPageSizeChanging] = useState(false);

    const params: GetProposalParams = { page, per_page: defaultTotalPages.value };
    if (searchType && searchValue.trim()) {
        params[searchType?.value as SearchField] = searchValue.trim();
    }

    const { isActuarialDepartment } = useProposalTypeData()
    const { 
        data: getRequestAmendments, 
        error: errorGetRequestAmendments, 
        isError: isErrorGetRequestAmendments, 
        isLoading: isLoadingGetRequestAmendments, 
        refetch: refetchGetRequestAmendments, 
        isFetching: isFetchingGetRequestAmendments 
    } = useGetRequestAmendmentsQuery(params, { skip: !isActuarialDepartment, refetchOnMountOrArgChange: true});
    const errorMessageGetRequestAmendments = getErrorMessage(errorGetRequestAmendments)
    
    useEffect(() => {
        setIsTyping(true);

        const timer = setTimeout(() => {
            setIsTyping(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue]);
    
    useEffect(() => {
        setPage(1);
    }, [defaultTotalPages.value]);
    
    useEffect(() => {
        if (isPageSizeChanging && !isFetchingGetRequestAmendments) {
            setIsPageSizeChanging(false);
        }
    }, [isFetchingGetRequestAmendments, isPageSizeChanging]);

    const handleChangePageSize = (value: TotalPages) => {
        setIsPageSizeChanging(true);
        setDefaultTotalPages(value);
        setPage(1);
    };

    const handleReload = () => {
        setPage(1);
        refetchGetRequestAmendments();
    };

    const loading =  isTyping || isLoadingGetRequestAmendments || isPageSizeChanging


    return {
        getRequestAmendments,
        normalizedOptions,
        errorMessageGetRequestAmendments,
        searchType: selectedSearchType,
        setSearchType: setSelectedSearchType,
        searchValue,
        setSearchValue,
        setPage,
        pagedData: getRequestAmendments?.data ?? [],
        loading,
        isErrorGetRequestAmendments,
        handleReload,
        pagination: getRequestAmendments?.pagination,

        defaultTotalPages,
        setDefaultTotalPages,
        proposalDefaultPageSize: totalPages,
        handleChangePageSize,
    }
}